import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, ShieldCheck, Zap, HandshakeIcon, Phone, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { COMPANY } from "@/data/content";
import { RC_PROFESSIONALE, VANTAGGI_RC, PROCESSO_RC } from "@/data/rcProfessionale";

const ICONS = { ShieldCheck, Zap, HandshakeIcon };

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-foreground/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="faq-question"
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-display text-xl sm:text-2xl leading-snug group-hover:text-primary transition-colors">{q}</span>
        <Plus className={`h-5 w-5 shrink-0 text-primary transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      {open && <p className="pb-6 -mt-1 text-muted-foreground leading-relaxed max-w-3xl">{a}</p>}
    </div>
  );
}

export default function RCProfessionale({ slug }) {
  const cfg = RC_PROFESSIONALE[slug];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cfg.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const empty = {
    nome: "",
    email: "",
    telefono: "",
    codice_fiscale: "",
    partita_iva: "",
    sede_legale: "",
    attivita: "",
    website: "",
    oggetto: cfg.formOggetto,
    messaggio: "",
    privacy: false,
  };

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Inserisci un nome valido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Inserisci un'email valida";
    if (form.telefono.trim().length < 6) e.telefono = "Inserisci un numero di telefono valido";
    const cf = form.codice_fiscale.trim();
    if (cf && !(/^[A-Za-z0-9]{11,16}$/).test(cf)) e.codice_fiscale = "CF non valido (11 o 16 caratteri)";
    const piva = form.partita_iva.trim();
    if (piva && !(/^\d{11}$/).test(piva)) e.partita_iva = "P.IVA composta da 11 cifre";
    if (!cf && !piva) e.codice_fiscale = "Inserisci almeno CF o Partita IVA";
    if (form.sede_legale.trim().length < 5) e.sede_legale = "Inserisci la sede dello studio (via, città, CAP)";
    if (form.attivita.trim().length < 20) e.attivita = "Descrivi con almeno 20 caratteri l'attività svolta";
    if (form.messaggio.trim().length < 10) e.messaggio = "Indica fatturato/onorari e massimale desiderato";
    if (!form.privacy) e.privacy = "Devi accettare l'informativa sulla privacy";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    // Honeypot: se compilato, è un bot — fingiamo il successo senza inviare nulla.
    if (form.website.trim()) {
      toast.success("Richiesta inviata", { description: "Un consulente XL Brokers ti richiama entro 24 ore lavorative." });
      setForm({ ...empty });
      return;
    }
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", { ...form, source: `Landing ${cfg.formOggetto} (Google Ads)` });
      toast.success("Richiesta inviata", { description: "Un consulente XL Brokers ti richiama entro 24 ore lavorative." });
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { form: slug });
      }
      setForm({ ...empty });
    } catch (e) {
      toast.error("Errore", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid={`page-${slug}`} className="pt-28">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A1F2E] text-white">
        <div className="absolute inset-0 -z-10">
          <img src={cfg.image} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F2E] via-[#0A1F2E]/85 to-[#0A1F2E]/40" />
        </div>

        <div className="container-x py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-semibold text-[#89E877]">
              {cfg.eyebrow}
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {cfg.h1.pre} <span className="italic text-[#89E877]">{cfg.h1.hi}</span> {cfg.h1.post}
            </h1>
            <p className="mt-8 max-w-xl text-lg text-white/75 leading-relaxed" dangerouslySetInnerHTML={{ __html: cfg.introHtml }} />

            <ul className="mt-10 grid sm:grid-cols-3 gap-6">
              {VANTAGGI_RC.map(({ icon, t, d }) => {
                const Icon = ICONS[icon];
                return (
                  <li key={t} className="border-l-2 border-[#89E877] pl-4">
                    <Icon className="h-5 w-5 text-[#89E877]" />
                    <p className="mt-3 font-display text-lg">{t}</p>
                    <p className="mt-1 text-sm text-white/65 leading-relaxed">{d}</p>
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#preventivo" data-testid="hero-cta-preventivo" className="inline-flex items-center gap-2 bg-[#89E877] text-[#0A1F2E] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white transition-colors">
                Richiedi una consulenza <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={`tel:${COMPANY.phone}`} data-testid="hero-cta-phone" className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-sm font-medium tracking-wide hover:bg-white hover:text-[#0A1F2E] transition-colors">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-5" id="preventivo">
            <form onSubmit={submit} className="bg-background text-foreground p-8 sm:p-10 border-t-2 border-[#89E877]" data-testid="lp-form">
              <span className="overline">Richiesta di consulenza</span>
              <h2 className="mt-3 font-display text-3xl leading-tight">
                Ricevi un'analisi<br /><span className="italic text-primary">su misura</span>
              </h2>

              <div className="mt-8 space-y-6">
                {/* Honeypot anti-spam — invisibile agli utenti, lasciato vuoto */}
                <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
                  <label htmlFor="lp-website">Non compilare questo campo</label>
                  <input
                    id="lp-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-nome">Nome / Ragione sociale dello studio*</label>
                  <input id="lp-nome" className="field-input" data-testid="lp-input-nome" value={form.nome} onChange={(e) => update("nome", e.target.value)} placeholder="Es. Studio Rossi & Associati" />
                  {errors.nome && <p className="mt-1 text-xs text-destructive">{errors.nome}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-email">Email*</label>
                  <input id="lp-email" type="email" className="field-input" data-testid="lp-input-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="nome@studio.it" />
                  {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="lp-tel">Telefono*</label>
                  <input id="lp-tel" className="field-input" data-testid="lp-input-telefono" value={form.telefono} onChange={(e) => update("telefono", e.target.value)} placeholder="+39 ..." />
                  {errors.telefono && <p className="mt-1 text-xs text-destructive">{errors.telefono}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label" htmlFor="lp-cf">Codice fiscale</label>
                    <input id="lp-cf" className="field-input" data-testid="lp-input-codice-fiscale" value={form.codice_fiscale} onChange={(e) => update("codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} autoComplete="off" />
                    {errors.codice_fiscale && <p className="mt-1 text-xs text-destructive">{errors.codice_fiscale}</p>}
                  </div>
                  <div>
                    <label className="field-label" htmlFor="lp-piva">Partita IVA</label>
                    <input id="lp-piva" className="field-input" data-testid="lp-input-partita-iva" value={form.partita_iva} onChange={(e) => update("partita_iva", e.target.value.replace(/\D/g, ""))} placeholder="12345678901" inputMode="numeric" maxLength={11} autoComplete="off" />
                    {errors.partita_iva && <p className="mt-1 text-xs text-destructive">{errors.partita_iva}</p>}
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-sede">Sede dello studio*</label>
                  <input id="lp-sede" className="field-input" data-testid="lp-input-sede-legale" value={form.sede_legale} onChange={(e) => update("sede_legale", e.target.value)} placeholder="Via Roma 10, 40128 Bologna (BO)" autoComplete="street-address" />
                  {errors.sede_legale && <p className="mt-1 text-xs text-destructive">{errors.sede_legale}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-attivita">Descrizione attività svolta*</label>
                  <textarea id="lp-attivita" rows={3} className="field-input resize-none" data-testid="lp-input-attivita" value={form.attivita} onChange={(e) => update("attivita", e.target.value)} placeholder={cfg.attivitaPlaceholder} />
                  {errors.attivita && <p className="mt-1 text-xs text-destructive">{errors.attivita}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="lp-msg">Fatturato/onorari annui e massimale desiderato*</label>
                  <textarea id="lp-msg" rows={3} className="field-input resize-none" data-testid="lp-input-messaggio" value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)} placeholder="Es. Onorari annui € 180.000, massimale desiderato € 2 mln, nessun sinistro negli ultimi 5 anni." />
                  {errors.messaggio && <p className="mt-1 text-xs text-destructive">{errors.messaggio}</p>}
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox id="lp-privacy" data-testid="lp-checkbox-privacy" checked={form.privacy} onCheckedChange={(v) => update("privacy", !!v)} className="mt-1" />
                  <label htmlFor="lp-privacy" className="text-xs text-muted-foreground leading-relaxed">
                    Accetto l'<Link to="/privacy" className="underline">informativa privacy</Link> ai sensi del GDPR (Reg. UE 2016/679).*
                  </label>
                </div>
                {errors.privacy && <p className="text-xs text-destructive">{errors.privacy}</p>}

                <button type="submit" disabled={loading} data-testid="lp-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] text-white px-6 py-4 text-sm font-semibold tracking-wide hover:bg-primary transition-colors disabled:opacity-50">
                  {loading ? "Invio in corso..." : "Invia la richiesta"} <ArrowUpRight className="h-4 w-4" />
                </button>
                <p className="text-[11px] text-muted-foreground text-center">Senza impegno · Ti ricontattiamo entro 24 ore lavorative</p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* COSA COPRE */}
      <section className="section-y container-x">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">Cosa copre</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
              Le garanzie della<br /><span className="italic text-primary">RC Professionale</span>
            </h2>
            <p className="mt-6 text-muted-foreground max-w-md">{cfg.copertureIntro}</p>
          </div>
          <ul className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {cfg.coperture.map((c) => (
              <li key={c} className="flex items-start gap-4 border-b border-foreground/10 pb-4">
                <Check className="h-5 w-5 mt-1 text-primary shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ALTRE SOLUZIONI */}
      <section className="bg-[#0A1F2E] text-white section-y">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6">
              <span className="overline text-[#89E877]">Le altre soluzioni per la tua professione</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">{cfg.altreTitle}</h2>
            </div>
            <p className="md:col-span-6 md:justify-self-end max-w-md text-white/70">{cfg.altreIntro}</p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {cfg.altre.map((s, i) => (
              <div key={s.t} data-testid={`soluzione-${i}`} className="bg-[#0A1F2E] p-8 hover:bg-[#0c2638] transition-colors">
                <p className="font-display text-4xl text-[#89E877]/40">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-6 font-display text-xl leading-tight">{s.t}</p>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#preventivo" className="inline-flex items-center gap-2 bg-[#89E877] text-[#0A1F2E] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white transition-colors" data-testid="mid-cta">
              Richiedi analisi personalizzata <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* PROCESSO */}
      <section className="section-y container-x">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">Come funziona</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">In 3 passi alla tua <span className="italic text-primary">consulenza</span>.</h2>
          </div>
          <ol className="md:col-span-8 space-y-10">
            {PROCESSO_RC.map((s, i) => (
              <li key={s.t} className="grid grid-cols-12 gap-6 border-t border-foreground/10 pt-8">
                <span className="col-span-2 md:col-span-1 font-display text-4xl text-primary/40">0{i + 1}</span>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-display text-2xl">{s.t}</h3>
                  <p className="mt-2 text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-muted/40" data-testid="faq-section">
        <div className="container-x grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">Domande frequenti</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
              Le risposte<br /><span className="italic text-primary">che cerchi</span>.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-md">
              Hai altri dubbi? Parla direttamente con un consulente XL Brokers.
            </p>
            <a href="#preventivo" data-testid="faq-cta" className="mt-8 inline-flex items-center gap-2 text-sm font-medium underline-link">
              Richiedi una consulenza <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="md:col-span-8">
            {cfg.faq.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-background border-t border-foreground/10 py-20">
        <div className="container-x grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">{cfg.ctaTitle}</h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl">{cfg.ctaText}</p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-wrap gap-4">
            <a href="#preventivo" className="btn-primary" data-testid="final-cta-form">Richiedi consulenza</a>
            <a href={`tel:${COMPANY.phone}`} className="btn-outline" data-testid="final-cta-phone">
              <Phone className="h-4 w-4" /> Chiamaci
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
